import React, { useState, useEffect, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import toast from "react-hot-toast";
import ItemService from "../../../../services/customer_appCommonService";
import ERFExtensionPaymentAdjustmentDetails from "./ERFExtensionPaymentAdjustmentDetails";
// eslint-disable-next-line
import Loader from "../../../../Components/Loader";
import { useSelector } from "react-redux";
import FailureModal from "../../../../Components/Modal/supplier_app/FailureModal";

//eslint-disable-next-line
const ERF_TYPES_WITH_SECURITY = ["Rent", "Sale & Rent"];

const ERFExtensionPaymentSection = ({
  documentNumber,
  allowAddBilling = true,
  allowAddSecurity = false,
  allowAddCreditNote = true,
  allowUpdatePayentButton = true,
  onPaymentSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [erfExtensionRecord, setErfExtensionRecord] = useState(null);
  //eslint-disable-next-line
  // const [showSecurity, setShowSecurity] = useState(false);
  const showSecurity = false;

  const userData = useSelector((state) => state?.user);
  const currentUserName =
    userData?.data?.firstName || userData?.data?.userId || "";

  const isServiceExecutive = useMemo(
    () =>
      userData?.data?.roles?.some((r) =>
        ["SERVICE-EXECUTIVE", "DISPATCH"].includes(r.roleId),
      ) ?? false,
    [userData],
  );

  const isFinanceExecutive = useMemo(
    () =>
      userData?.data?.roles?.some((r) => r.roleId === "FI-EXECUTIVE") ?? false,
    [userData],
  );

  const isSalesAdmin = useMemo(
    () =>
      userData?.data?.roles?.some((r) => r.roleId === "SALES-ADMIN") ?? false,
    [userData],
  );

  const [isAdminOrPCD, setIsAdminOrPCD] = useState(false);

  // Existing rows passed to child as read-only props (no local_id)
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [securityHistory, setSecurityHistory] = useState([]);
  const [creditNoteHistory, setCreditNoteHistory] = useState([]);
  const [serviceExecutiveList, setServiceExecutiveList] = useState([]);

  const [collectionStatus, setCollectionStatus] = useState(null);

  // Full merged arrays (existing + new rows) — updated live from child
  const [latestData, setLatestData] = useState({
    paymentHistory: [],
    securityHistory: [],
    creditNoteHistory: [],
  });

  const [getShowFailureModal, setShowFailureModal] = useState(false);
  const [getErrorFields, setErrorFields] = useState([]);
  const [failureModalData] = useState({
    title: "Validation Error",
    ErrorMsg: "Please check the required fields.",
  });

  // ── FETCH ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (documentNumber) fetchERFExtensionData();
    // eslint-disable-next-line
  }, [documentNumber]);

  useEffect(() => {
    if (userData?.data?.roles) {
      const isPCDOrAdmin = userData?.data?.roles.some((r) =>
        ["SALES-ADMIN", "SALES-PCD"].includes(r.roleId),
      );
      setIsAdminOrPCD(isPCDOrAdmin);
    }
  }, [userData]);

  const fetchERFExtensionData = async () => {
    setLoading(true);
    try {
      const payload = {
        queryName: "ERFExtDetail",
        documentNumber: documentNumber,
        timeZone: "Asia/Calcutta",
      };

      const response = await ItemService.generateQuery(payload);

      if (
        response?.status === 200 &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        const record = response.data[response.data.length - 1];
        setErfExtensionRecord(record);
        // const hasSecuritySection = ERF_TYPES_WITH_SECURITY.includes(
        //   record.erfType ?? "",
        // );
        // setShowSecurity(hasSecuritySection);
        buildDisplayState(record, false);
      } else {
        toast.error("No ERF data found for this document");
      }
    } catch (err) {
      console.error("ERFExtDetail fetch error:", err);
      toast.error("Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    const fetchExecutives = async () => {
      try {
        const response = await ItemService.generateQuery({
          queryName: "get_ServiceProvider_list",
          page: 1,
          per_page: 999,
          timeZone: "Asia/Calcutta",
        });
        if (response?.status === 200 && Array.isArray(response.data)) {
          setServiceExecutiveList(
            response.data.map((item) => ({
              id: item.userId,
              name: item.firstName || item.name,
              code: item.userId,
            })),
          );
        }
      } catch (err) {
        console.error("Failed to load service executives", err);
      }
    };
    fetchExecutives();
  }, []);

  const handleConfirmRow = async (tableKey, rowIndex, role, { remarks }) => {
    const tableMap = {
      billing: { rows: paymentHistory, setter: setPaymentHistory },
      security: { rows: securityHistory, setter: setSecurityHistory },
      creditNote: { rows: creditNoteHistory, setter: setCreditNoteHistory },
    };

    const { rows, setter } = tableMap[tableKey];
    const row = rows[rowIndex];
    if (!row) return;

    const confirmationKey =
      role === "serviceExecutive" ? "seConfirmation" : "financeConfirmation";

    const updatedRow = {
      ...row,
      [confirmationKey]: {
        confirmed: true,
        remarks,
        confirmedBy: currentUserName,
        confirmedAt: new Date().toISOString(),
      },
    };

    const updatedRows = rows.map((r, i) => (i === rowIndex ? updatedRow : r));

    setLoading(true);
    try {
      const isRefundClearTrigger =
        role === "financeExecutive" &&
        tableKey === "creditNote" &&
        row.status === "Refund";

      const isCreditNoteClearTrigger =
        role === "financeExecutive" &&
        tableKey === "creditNote" &&
        row.status === "Credit Note";

      const payload = {
        equipmentRentNr: documentNumber,
        equipmentNr: erfExtensionRecord.equipmentNr,
        paymentHistory: tableKey === "billing" ? updatedRows : paymentHistory,
        securityHistory:
          tableKey === "security" ? updatedRows : securityHistory,
        creditNoteHistory:
          tableKey === "creditNote" ? updatedRows : creditNoteHistory,
        ...(isRefundClearTrigger && { refundAmountIndicator: false }),
        ...(isCreditNoteClearTrigger && { creditNoteIndicator: false }),
      };

      // ← only difference from ERFPaymentSection — uses updateExtendEquipmentRent
      await ItemService.updateExtendEquipmentRent(payload);
      toast.success(
        role === "serviceExecutive" ? "Payment confirmed" : "Payment approved",
      );
      setter(updatedRows);
    } catch {
      toast.error("Failed to save confirmation");
    } finally {
      setLoading(false);
    }
  };

  // ── BUILD DISPLAY STATE ───────────────────────────────────────────────────
  const buildDisplayState = (record, hasSecuritySection) => {
    setCollectionStatus(record.financeCollectionConfirmation ?? null);
    // Strips local_id from saved rows so they load as read-only in the child.
    // Child treats rows WITHOUT local_id as historical (read-only).
    const stripLocalId = (rows) =>
      (rows ?? []).map(({ local_id, ...rest }) => rest);

    // ── Case A: paymentHistory already exists on this ERF ─────────────────
    if (record.paymentHistory?.length > 0) {
      const billing = stripLocalId(record.paymentHistory);
      const security = hasSecuritySection
        ? stripLocalId(record.securityHistory)
        : [];
      const creditNote = stripLocalId(record.creditNoteHistory);

      setPaymentHistory(billing);
      setSecurityHistory([]);
      setCreditNoteHistory(creditNote);
      setLatestData({
        paymentHistory: billing,
        securityHistory: security,
        creditNoteHistory: creditNote,
      });
      return;
    }

    // ── Case B: First time — build initial rows from flat ERF fields ───────
    // No local_id on these rows → they display as read-only in the table.

    const initialBillingRow = {
      paymentDate: record.extensionDate ?? record.orderDate ?? "",
      salePoint: record.salePoint ?? "",
      receivedLocation: record.salePoint ?? "",
      billingTotalAmt: record.netAmount ?? 0,
      paymentMode: record.paymentMode ?? "",
      billingReceivedAmt: record.receivedAmount ?? 0,
      chequeNo: record.chequeNo || "NA",
      cardNo: record.cardTransNo || "NA",
      bank: record.bankName || "NA",
      billingBalanceAmt: record.balanceAmount ?? 0, // ← full amount outstanding
      sellerName: "",
    };

    const initialSecurityRow = hasSecuritySection
      ? {
          paymentDate: record.orderDate ?? "",
          salePoint: record.salePoint ?? "",
          receivedLocation: record.salePoint ?? "",
          securityTotalAmt: record.rentSecurityTotal ?? 0, // total security due
          paymentMode: record.securityAmtMode ?? "",
          securityReceivedAmt: record.receivedSecurityAmt ?? 0, // security collected on rent day
          chequeNo: record.securityChequeNo || "NA",
          cardNo: record.securityCardTransNo || "NA",
          bank: record.securityBankName || "NA",
          securityBalanceAmt: record.balanceSecurityAmt ?? 0, // outstanding → drives C
          sellerName: "",
        }
      : null;

    const billing = [initialBillingRow];
    const security = initialSecurityRow ? [initialSecurityRow] : [];
    const creditNote = stripLocalId(record.creditNoteHistory);

    setPaymentHistory(billing);
    setSecurityHistory(security);
    setCreditNoteHistory(creditNote);
    setLatestData({
      paymentHistory: billing,
      securityHistory: [],
      creditNoteHistory: creditNote,
    });
  };

  // ── RECEIVE LIVE UPDATES FROM CHILD ───────────────────────────────────────
  const handleDataChange = (updatedData) => {
    setLatestData(updatedData);
  };

  const validateNewRows = (billingRows) => {
    const errors = [];

    billingRows
      .filter((r) => r.local_id) // Only validate newly added rows
      .forEach((row, idx) => {
        const label = `Billing row ${idx + 1}`;
        if (!row.paymentDate) errors.push(`${label}: Payment Date is required`);
        if (!row.salePoint?.toString().trim())
          errors.push(`${label}: Sale Point is required`);
        if (!row.paymentMode) errors.push(`${label}: Payment Mode is required`);
        if (
          row.billingReceivedAmt === "" ||
          row.billingReceivedAmt === null ||
          row.billingReceivedAmt === undefined
        )
          errors.push(`${label}: Received Amount is required`);
      });

    return errors;
  };

  // ── UPDATE PAYMENT ────────────────────────────────────────────────────────
  const handleUpdatePayment = async () => {
    if (!erfExtensionRecord) {
      toast.error("No ERF Extension record loaded");
      return;
    }
    if (!latestData.paymentHistory?.length) {
      toast.error("No billing records to save");
      return;
    }

    const validationErrors = validateNewRows(latestData.paymentHistory);

    if (validationErrors.length > 0) {
      setErrorFields(validationErrors);
      setShowFailureModal(true); // Show the modal with errors
      return;
    }

    setLoading(true);
    try {
      const { paymentHistory, securityHistory, creditNoteHistory } = latestData;

      // ── Recalculate summary values ──────────────────────────────────────
      // A = total received from patient
      const totalBillingReceived = paymentHistory.reduce(
        (sum, row) => sum + (parseFloat(row.billingReceivedAmt) || 0),
        0,
      );
      const totalSecurityReceived = securityHistory.reduce(
        (sum, row) => sum + (parseFloat(row.securityReceivedAmt) || 0),
        0,
      );

      const A = totalBillingReceived + totalSecurityReceived;

      // // B = total credit notes issued
      // const B = creditNoteHistory.reduce(
      //   (sum, row) => sum + (parseFloat(row.amount) || 0),
      //   0,
      // );
      // B = credit notes only (rows with status "Refund" are NOT counted here)
      const B = creditNoteHistory
        .filter((row) => row.status !== "Refund")
        .reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);

      // Refund = ONLY rows the user explicitly marked as "Refund"
      const B_refund = creditNoteHistory
        .filter((row) => row.status === "Refund")
        .reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);

      // C = last billing balance + last security balance
      const lastBillingBalance =
        parseFloat(paymentHistory.at(-1)?.billingBalanceAmt ?? 0) || 0;
      const lastSecurityBalance = showSecurity
        ? parseFloat(securityHistory.at(-1)?.securityBalanceAmt ?? 0) || 0
        : 0;
      const C = lastBillingBalance + lastSecurityBalance;

      const balAmt = Math.max(0, parseFloat((C - B).toFixed(2)));
      // const returnedAmount = Math.max(0, parseFloat((B - C).toFixed(2)));
      const returnedAmount = parseFloat(B_refund.toFixed(2));

      const hasNewRows =
        paymentHistory.some((r) => r.local_id) ||
        securityHistory.some((r) => r.local_id);

      const paymentSummary = {
        A: parseFloat(A.toFixed(2)),
        B: parseFloat(B.toFixed(2)),
        C: parseFloat(C.toFixed(2)),
        totalBillingReceived: parseFloat(totalBillingReceived.toFixed(2)),
        totalSecurityReceived: parseFloat(totalSecurityReceived.toFixed(2)),
        totalCreditNote: parseFloat(B.toFixed(2)),
        balAmt,
        returnedAmount,
      };

      // ── Build payload ───────────────────────────────────────────────────
      const payloadData = {
        documentNumber: documentNumber, // ← extension number
        equipmentNr: erfExtensionRecord.equipmentNr,
        equipmentRentNr: documentNumber,

        paymentHistory: paymentHistory,
        securityHistory: securityHistory,
        creditNoteHistory: creditNoteHistory,
        paymentSummary: paymentSummary,
        ...(hasNewRows && { financeCollectionConfirmation: "Not Confirmed" }),
      };

      await ItemService.updateExtendEquipmentRent(payloadData);
      toast.success("Payment history updated successfully");

      // ── Notify parent modal that payment was saved ─────────────────────────
      onPaymentSaved?.(payloadData.paymentHistory);

      //-----------Reload so newly saved rows come back as read-only history rows
      await fetchERFExtensionData();
    } catch (err) {
      console.error("Update payment error:", err);
      toast.error("Failed to update payment history");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCollection = async () => {
    setLoading(true);
    try {
      const obj = {
        documentNumber: erfExtensionRecord.documentNumber,
        equipmentNr: erfExtensionRecord.equipmentNr,
        equipmentRentNr: erfExtensionRecord.documentNumber,
        financeCollectionConfirmation: "Confirmed",
      };
      await ItemService.updateExtendEquipmentRent(obj);
      setCollectionStatus("Confirmed");
      toast.success("Collection confirmed");
    } catch {
      toast.error("Failed to confirm collection");
    } finally {
      setLoading(false);
    }
  };

  const hasReceivedEntries =
    paymentHistory.some((r) => parseFloat(r.billingReceivedAmt) > 0) ||
    securityHistory.some((r) => parseFloat(r.securityReceivedAmt) > 0);

  const hasUnsavedRows =
    latestData.paymentHistory?.some((r) => r.local_id) ||
    latestData.securityHistory?.some((r) => r.local_id) ||
    latestData.creditNoteHistory?.some((r) => r.local_id);

  // ── RENDER ────────────────────────────────────────────────────────────────
  // if (loading) return <Loader isLoading={loading} className="text-center" />;

  // ── ASSIGNED SE PERMISSION ────────────────────────────────────────────────
  const assignedSEUserId = erfExtensionRecord?.serviceProviderCode || "";
  const currentUserId = userData?.data?.userId || "";
  const financeNotConfirmed = collectionStatus !== "Confirmed";

  const canSEAddPayment =
    isServiceExecutive &&
    !!assignedSEUserId &&
    assignedSEUserId === currentUserId;

  const effectiveAllowAddBilling =
    (isSalesAdmin || canSEAddPayment) && financeNotConfirmed && allowAddBilling;
  const effectiveAllowAddSecurity =
    (isSalesAdmin || canSEAddPayment) &&
    financeNotConfirmed &&
    allowAddSecurity;

  return (
    <div>
      <ERFExtensionPaymentAdjustmentDetails
        showSecurity={showSecurity}
        paymentHistory={paymentHistory}
        securityHistory={securityHistory}
        creditNoteHistory={creditNoteHistory}
        onDataChange={handleDataChange}
        serviceExecutiveList={serviceExecutiveList}
        isServiceExecutive={isServiceExecutive}
        isFinanceExecutive={isFinanceExecutive}
        onConfirmRow={handleConfirmRow}
        allowAddBilling={effectiveAllowAddBilling}
        allowAddSecurity={effectiveAllowAddSecurity}
        allowAddCreditNote={allowAddCreditNote}
        defaultSalePoint={erfExtensionRecord?.salePoint ?? ""}
        defaultCostCenter={erfExtensionRecord?.costCenter ?? ""}
        isAdminOrPCD={isAdminOrPCD}
      />

      {/* <Row className="justify-content-end mt-2 mb-4 pe-3"> */}
      {hasReceivedEntries && (
        // <Row className="align-items-center mx-1 mb-3 p-2 border rounded bg-light">
        <Row className="align-items-center mx-1 mb-3 p-2 border rounded bg-light justify-content-between">
          <Col xs={6} md={3} className="pb-2 ps-2">
            <strong className="me-2">Finance Collection:</strong>
            {collectionStatus === "Confirmed" ? (
              <span className="badge bg-success">&#10003; Confirmed</span>
            ) : (
              <span className="badge bg-warning text-dark">Not Confirmed</span>
            )}
          </Col>
          {isFinanceExecutive && collectionStatus !== "Confirmed" && (
            <Col xs={6} md={2} className="pb-2 ps-2">
              <button
                className="btnTable w-100"
                onClick={handleConfirmCollection}
                disabled={loading}
              >
                Confirm Collection
              </button>
            </Col>
          )}
        </Row>
      )}

      <Row className="justify-content-end mt-2 mb-4 pe-3">
        <Col xs={6} md={2} className="pb-2 ps-2">
          {true && (
            <button
              className="btnTable w-100"
              onClick={handleUpdatePayment}
              disabled={loading || !erfExtensionRecord || !hasUnsavedRows}
            >
              <i className="bi bi-save me-1" /> Save Payment
            </button>
          )}
        </Col>
      </Row>

      <FailureModal
        show={getShowFailureModal}
        onHide={() => setShowFailureModal(false)}
        failuremodaldata={failureModalData}
        requiredfields={getErrorFields}
      />
    </div>
  );
};

export default ERFExtensionPaymentSection;
